import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { CartItem, OrderType, LOCATION_CODES } from "@/types/orders";

// Session mode types for anti-fraud system
export type SessionMode = "mesa" | "pickup" | "escaparate";

interface CartContextType {
  items: CartItem[];
  orderType: OrderType | null;
  tableNumber: number | null;
  locationCode: string | null;
  sessionMode: SessionMode;
  isValidSession: boolean;
  addItem: (item: Omit<CartItem, "quantity">, removedIngredients?: string[]) => void;
  removeItem: (id: string, removedIngredients?: string[]) => void;
  updateQuantity: (id: string, quantity: number, removedIngredients?: string[]) => void;
  clearCart: () => void;
  setOrderType: (type: OrderType) => void;
  setTableNumber: (num: number | null) => void;
  setLocationCode: (code: string | null) => void;
  total: number;
  itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

// Session storage keys for anti-fraud system
const SESSION_MODE_KEY = "session_mode";
const SESSION_EXPIRY_KEY = "session_mode_expiry";
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

// Normalize location code from various formats
const normalizeLocationCode = (code: string): string | null => {
  const normalizedInput = code.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
  
  const legacyCodeMapping: Record<string, string> = {
    "felo-monzon": "siete-palmas",
    "pintor-felo-monzon": "siete-palmas",
    "felo": "siete-palmas",
    "siete_palmas": "siete-palmas",
    "sietepalmas": "siete-palmas",
    "7-palmas": "siete-palmas",
    "7palmas": "siete-palmas",
    "jordan": "lomo-frailes",
    "lomo_frailes": "lomo-frailes",
    "lomo-los-frailes": "lomo-frailes",
    "lomo_los_frailes": "lomo-frailes",
    "lomolosfrailes": "lomo-frailes",
    "lomofrailes": "lomo-frailes",
    "siete-palmas": "siete-palmas",
    "lomo-frailes": "lomo-frailes",
  };
  
  return legacyCodeMapping[normalizedInput] || 
         (LOCATION_CODES[normalizedInput] ? normalizedInput : null);
};

// Check if session is still valid (not expired)
const isSessionValid = (): boolean => {
  const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
  if (!expiry) return false;
  return Date.now() < parseInt(expiry, 10);
};

// Save session mode with expiry
const saveSessionMode = (mode: SessionMode) => {
  sessionStorage.setItem(SESSION_MODE_KEY, mode);
  sessionStorage.setItem(SESSION_EXPIRY_KEY, (Date.now() + SESSION_DURATION_MS).toString());
};

// Load session mode if not expired
const loadSessionMode = (): SessionMode | null => {
  if (!isSessionValid()) {
    sessionStorage.removeItem(SESSION_MODE_KEY);
    sessionStorage.removeItem(SESSION_EXPIRY_KEY);
    return null;
  }
  return sessionStorage.getItem(SESSION_MODE_KEY) as SessionMode | null;
};

export const CartProvider = ({ children }: CartProviderProps) => {
  const [searchParams] = useSearchParams();
  
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = sessionStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [orderType, setOrderType] = useState<OrderType | null>(() => {
    const saved = sessionStorage.getItem("orderType");
    return saved ? (saved as OrderType) : null;
  });
  const [tableNumber, setTableNumber] = useState<number | null>(() => {
    const saved = sessionStorage.getItem("tableNumber");
    return saved ? parseInt(saved) : null;
  });
  const [locationCode, setLocationCode] = useState<string | null>(() => {
    const saved = sessionStorage.getItem("locationCode");
    return saved || null;
  });
  
  // Anti-fraud session mode state
  const [sessionMode, setSessionMode] = useState<SessionMode>(() => {
    return loadSessionMode() || "escaparate";
  });
  const [isValidSession, setIsValidSession] = useState<boolean>(() => {
    return loadSessionMode() !== null && loadSessionMode() !== "escaparate";
  });

  // Read store/sede and mesa parameters from URL (for QR codes)
  // AND detect session mode for anti-fraud system
  useEffect(() => {
    // Support both 'store' and 'sede' parameters for backward compatibility
    let storeParam = searchParams.get("store") || searchParams.get("sede");
    const mesaParam = searchParams.get("mesa");
    const typeParam = searchParams.get("type");
    
    // CASE A: Mesa parameter detected (QR de mesa) - allows full ordering
    if (mesaParam) {
      const mesaNumber = parseInt(mesaParam, 10);
      if (!isNaN(mesaNumber) && mesaNumber >= 1 && mesaNumber <= 99) {
        console.log("[CartContext] MESA MODE - QR scanned with table:", mesaNumber);
        
        // Set session as valid for ordering
        setSessionMode("mesa");
        setIsValidSession(true);
        saveSessionMode("mesa");
        
        // Set order details
        setOrderType("mesa");
        setTableNumber(mesaNumber);
        sessionStorage.setItem("orderType", "mesa");
        sessionStorage.setItem("tableNumber", mesaNumber.toString());
      }
    }
    
    // CASE B: Pickup/takeaway parameter detected (QR de mostrador)
    if (typeParam === "pickup" || typeParam === "llevar") {
      console.log("[CartContext] PICKUP MODE - QR scanned for takeaway");
      
      // Set session as valid for ordering
      setSessionMode("pickup");
      setIsValidSession(true);
      saveSessionMode("pickup");
      
      // Set order type to takeaway
      setOrderType("llevar");
      sessionStorage.setItem("orderType", "llevar");
    }
    
    // Handle location code normalization
    if (storeParam) {
      const finalCode = normalizeLocationCode(storeParam);
      
      if (finalCode && LOCATION_CODES[finalCode]) {
        console.log("[CartContext] Setting locationCode from URL:", storeParam, "->", finalCode);
        setLocationCode(finalCode);
        sessionStorage.setItem("locationCode", finalCode);
      } else {
        console.warn("[CartContext] Unknown location code from QR:", storeParam);
      }
    }
    
    // If no QR params but session exists, restore session mode
    if (!mesaParam && !typeParam) {
      const savedMode = loadSessionMode();
      if (savedMode && savedMode !== "escaparate") {
        setSessionMode(savedMode);
        setIsValidSession(true);
      } else {
        // No valid session - ESCAPARATE mode
        setSessionMode("escaparate");
        setIsValidSession(false);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    sessionStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (orderType) {
      sessionStorage.setItem("orderType", orderType);
    } else {
      sessionStorage.removeItem("orderType");
    }
  }, [orderType]);

  useEffect(() => {
    if (tableNumber !== null) {
      sessionStorage.setItem("tableNumber", tableNumber.toString());
    } else {
      sessionStorage.removeItem("tableNumber");
    }
  }, [tableNumber]);

  useEffect(() => {
    if (locationCode) {
      sessionStorage.setItem("locationCode", locationCode);
    } else {
      sessionStorage.removeItem("locationCode");
    }
  }, [locationCode]);

  // Generate unique cart key for item (includes customizations)
  const getCartItemKey = (id: string, removedIngredients?: string[]): string => {
    if (!removedIngredients || removedIngredients.length === 0) return id;
    const sortedRemovals = [...removedIngredients].sort().join(",");
    return `${id}__${sortedRemovals}`;
  };

  const addItem = (item: Omit<CartItem, "quantity">, removedIngredients?: string[]) => {
    // SECURITY: Block adding items if session is not valid
    // EXCEPTION: Allow "encargo" category items (custom orders) without session validation
    const isEncargoItem = item.category === "Tarta por Encargo" || item.category === "encargos" || item.id.startsWith("encargo-");
    
    if (!isValidSession && !isEncargoItem) {
      console.warn("[CartContext] Blocked addItem - no valid session (not an encargo)");
      return;
    }
    
    // Validate price is not zero or invalid
    if (!item.price || item.price <= 0) {
      console.error("[CartContext] Blocked addItem - invalid price:", item.price, "for item:", item.name);
      return;
    }
    
    console.log("[CartContext] Adding item:", item.name, "Price:", item.price, "Category:", item.category);
    
    const cartKey = getCartItemKey(item.id, removedIngredients);
    
    setItems((prev) => {
      // Find existing item with same ID AND same customizations
      const existing = prev.find((i) => {
        const existingKey = getCartItemKey(i.id, i.removedIngredients);
        return existingKey === cartKey;
      });
      
      if (existing) {
        return prev.map((i) => {
          const itemKey = getCartItemKey(i.id, i.removedIngredients);
          return itemKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i;
        });
      }
      
      return [...prev, { 
        ...item, 
        quantity: 1,
        removedIngredients: removedIngredients && removedIngredients.length > 0 ? removedIngredients : undefined
      }];
    });
  };

  const removeItem = (id: string, removedIngredients?: string[]) => {
    const cartKey = getCartItemKey(id, removedIngredients);
    setItems((prev) => prev.filter((i) => {
      const itemKey = getCartItemKey(i.id, i.removedIngredients);
      return itemKey !== cartKey;
    }));
  };

  const updateQuantity = (id: string, quantity: number, removedIngredients?: string[]) => {
    if (quantity <= 0) {
      removeItem(id, removedIngredients);
      return;
    }
    
    const cartKey = getCartItemKey(id, removedIngredients);
    setItems((prev) =>
      prev.map((i) => {
        const itemKey = getCartItemKey(i.id, i.removedIngredients);
        return itemKey === cartKey ? { ...i, quantity } : i;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    setOrderType(null);
    setTableNumber(null);
    // Don't clear locationCode or sessionMode - they persist for the session
    sessionStorage.removeItem("cart");
    sessionStorage.removeItem("orderType");
    sessionStorage.removeItem("tableNumber");
  };

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        orderType,
        tableNumber,
        locationCode,
        sessionMode,
        isValidSession,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        setOrderType,
        setTableNumber,
        setLocationCode,
        total,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
